import { type ELeadStage } from 'src/services/api/leads';

export enum EPlaybookTrigger {
  LeadUnresponsive = 'lead_unresponsive',
  TourStarted = 'tour_started',
  IncomingSms = 'incoming-sms',
  IncomingEmail = 'incoming-email',
  IncomingFacebookMessage = 'incoming-facebook-message',
  LeadIngested = 'lead_ingested',
  Manual = 'manual',
}

export const playbookTriggers = Object.values(EPlaybookTrigger);

type IPlaybookTriggerBase<
  T extends EPlaybookTrigger = EPlaybookTrigger,
  TParams = undefined,
> = {
  type: T;
  run_limit_per_lead: number;
  params?: TParams extends undefined ? never : TParams;
};

type ILeadUnresponsivePlaybookTriggerParams = {
  time_unit: 'days' | 'hours' | 'minutes';
  time_value: number;
};
type ILeadUnresponsivePlaybookTrigger = IPlaybookTriggerBase<
  EPlaybookTrigger.LeadUnresponsive,
  ILeadUnresponsivePlaybookTriggerParams
>;

type ITourStartedPlaybookTriggerParams = {
  time_offset_type: 'before' | 'after';
  time_offset_unit: 'days' | 'hours' | 'minutes';
  time_offset_value: number;
};
type ITourStartedPlaybookTrigger = IPlaybookTriggerBase<
  EPlaybookTrigger.TourStarted,
  ITourStartedPlaybookTriggerParams
>;

type IIncomingSmsPlaybookTrigger =
  IPlaybookTriggerBase<EPlaybookTrigger.IncomingSms>;

type IIncomingEmailPlaybookTrigger =
  IPlaybookTriggerBase<EPlaybookTrigger.IncomingEmail>;

type IIncomingFacebookMessagePlaybookTrigger =
  IPlaybookTriggerBase<EPlaybookTrigger.IncomingFacebookMessage>;

type ILeadIngestedPlaybookTrigger =
  IPlaybookTriggerBase<EPlaybookTrigger.LeadIngested>;

type IManualPlaybookTrigger = IPlaybookTriggerBase<EPlaybookTrigger.Manual>;

export type IPlaybookTrigger =
  | ILeadUnresponsivePlaybookTrigger
  | ITourStartedPlaybookTrigger
  | IIncomingSmsPlaybookTrigger
  | IIncomingEmailPlaybookTrigger
  | IIncomingFacebookMessagePlaybookTrigger
  | ILeadIngestedPlaybookTrigger
  | IManualPlaybookTrigger;

export enum EPlaybookStepTypeGroup {
  TriggerFilter = 'trigger-filter',
  Filter = 'filter',
  Action = 'action',
}

export const playbookStepTypeGroups = Object.values(EPlaybookStepTypeGroup);

export enum EPlaybookTriggerFilter {
  IsFirstMessageInThread = 'is-first-message-in-thread',
  SpecificProperty = 'specific-property',
  HasProperty = 'has-property',
  HasPropertyTourLink = 'has-property-tour-link',
  HasPropertyApplicationLink = 'has-property-application-link',
  LeadInStage = 'lead_in_stage',
  PropertyLabels = 'property_labels',
  NoShowingsBooked = 'no-showings-booked',
}

export const playbookTriggerFilters = Object.values(EPlaybookTriggerFilter);

export enum EPlaybookFilter {
  IsLastMessageFromLandlord = 'is-last-message-from-landlord',
  NoMessageHistory = 'no-message-history',
  HasProperty = 'has-property',
  SpecificProperty = 'specific-property',
  HasTourLink = 'has-property-tour-link',
  HasApplicationLink = 'has-property-application-link',
  LeadInStage = 'lead_in_stage',
  PropertyLabels = 'property_labels',
  NoShowingsBooked = 'no-showings-booked',
}

export const playbookFilters = Object.values(EPlaybookFilter);

export enum EPlaybookAction {
  SendMessage = 'send_message',
  SendSnippet = 'send_snippet',
  UpdateLabels = 'update_labels'
}

export const playbookActions = Object.values(EPlaybookAction);

type IPlaybookTriggerFilterBase<
  T extends EPlaybookTriggerFilter,
  TParams = never,
> = {
  type_group: EPlaybookStepTypeGroup.TriggerFilter;
  type: T;
  params: TParams;
};

type IPlaybookTriggerFilterIsFirstMessageInThread =
  IPlaybookTriggerFilterBase<EPlaybookTriggerFilter.IsFirstMessageInThread>;

type IPlaybookTriggerFilterHasProperty =
  IPlaybookTriggerFilterBase<EPlaybookTriggerFilter.HasProperty>;

type IPlaybookTriggerFilterHasTourLink =
  IPlaybookTriggerFilterBase<EPlaybookTriggerFilter.HasPropertyTourLink>;

type IPlaybookTriggerFilterHasPropertyApplicationLink =
  IPlaybookTriggerFilterBase<EPlaybookTriggerFilter.HasPropertyApplicationLink>;

type IPlaybookTriggerFilterSpecificPropertyParams = { property_id: number };
type IPlaybookTriggerFilterSpecificProperty = IPlaybookTriggerFilterBase<
  EPlaybookTriggerFilter.SpecificProperty,
  IPlaybookTriggerFilterSpecificPropertyParams
>;

type IPlaybookTriggerFilterLeadStageParams = { lead_stages: ELeadStage[] };
type IPlaybookTriggerFilterLeadStage = IPlaybookTriggerFilterBase<
  EPlaybookTriggerFilter.LeadInStage,
  IPlaybookTriggerFilterLeadStageParams
>;

type IPlaybookTriggerFilterPropertyLabelParams = { property_label_ids: number[] };
type IPlaybookTriggerFilterPropertyLabel = IPlaybookTriggerFilterBase<
  EPlaybookTriggerFilter.PropertyLabels,
  IPlaybookTriggerFilterPropertyLabelParams
  >;

type IPlaybookTriggerFilterNoShowingsBooked = IPlaybookTriggerFilterBase<
  EPlaybookTriggerFilter.NoShowingsBooked
>;

type IPlaybookTriggerFilter =
  | IPlaybookTriggerFilterIsFirstMessageInThread
  | IPlaybookTriggerFilterHasProperty
  | IPlaybookTriggerFilterSpecificProperty
  | IPlaybookTriggerFilterHasTourLink
  | IPlaybookTriggerFilterHasPropertyApplicationLink
  | IPlaybookTriggerFilterLeadStage
  | IPlaybookTriggerFilterPropertyLabel
  | IPlaybookTriggerFilterNoShowingsBooked;

type IPlaybookFilterBase<T extends EPlaybookFilter, TParams = never> = {
  type_group: EPlaybookStepTypeGroup.Filter;
  type: T;
  params: TParams;
};

type IPlaybookFilterIsLastMessageFromLandlord =
  IPlaybookFilterBase<EPlaybookFilter.IsLastMessageFromLandlord>;


type IPlaybookFilterNoMessageHistory =
  IPlaybookFilterBase<EPlaybookFilter.NoMessageHistory>;

type IPlaybookFilterSpecificPropertyParams = { property_id: number };
type IPlaybookFilterSpecificProperty = IPlaybookFilterBase<
  EPlaybookFilter.SpecificProperty,
  IPlaybookFilterSpecificPropertyParams
>;

type IPlaybookFilterHasProperty =
  IPlaybookFilterBase<EPlaybookFilter.HasProperty>;

type IPlaybookFilterHasTourLink =
  IPlaybookFilterBase<EPlaybookFilter.HasTourLink>;

type IPlaybookFilterHasApplicationLink =
  IPlaybookFilterBase<EPlaybookFilter.HasApplicationLink>;

type IPlaybookFilterNoShowingsBookedParams = { property_id: number };
type IPlaybookFilterNoShowingsBooked = IPlaybookFilterBase<
  EPlaybookFilter.NoShowingsBooked,
  IPlaybookFilterNoShowingsBookedParams
>;



type IPlaybookFilterLeadStageParams = { lead_stages: ELeadStage[] };
type IPlaybookFilterLeadStage = IPlaybookFilterBase<
  EPlaybookFilter.LeadInStage,
  IPlaybookFilterLeadStageParams
>;

type IPlaybookFilterPropertyLabelParams = { property_label_ids: number[] };
type IPlaybookFilterPropertyLabel = IPlaybookFilterBase<
  EPlaybookFilter.PropertyLabels,
  IPlaybookFilterPropertyLabelParams
>;

type IPlaybookFilter =
  | IPlaybookFilterIsLastMessageFromLandlord
  | IPlaybookFilterNoMessageHistory
  | IPlaybookFilterHasProperty
  | IPlaybookFilterSpecificProperty
  | IPlaybookFilterHasTourLink
  | IPlaybookFilterHasApplicationLink
  | IPlaybookFilterNoShowingsBooked
  | IPlaybookFilterLeadStage
  | IPlaybookFilterPropertyLabel;

type IPlaybookActionBase<T extends EPlaybookAction, TParams = never> = {
  type_group: EPlaybookStepTypeGroup.Action;
  type: T;
  params: TParams;
};

type IPlaybookActionSendMessageParams = {
  channel: string;
  message: string;
};
type IPlaybookActionSendMessage = IPlaybookActionBase<
  EPlaybookAction.SendMessage,
  IPlaybookActionSendMessageParams
>;

type IPlaybookActionSendSnippetParams = {
  message: string;
};
type IPlaybookActionSendSnippet = IPlaybookActionBase<
  EPlaybookAction.SendSnippet,
  IPlaybookActionSendSnippetParams
>;

type IPlaybookActionUpdateLabelsParams = {
  add_label_ids: number[];
  delete_label_ids: number[];
  delete_existing_labels: boolean;
}

type IPlaybookActionUpdateLabel = IPlaybookActionBase<
  EPlaybookAction.UpdateLabels,
  IPlaybookActionUpdateLabelsParams
>;


type IPlaybookAction = IPlaybookActionSendMessage | IPlaybookActionSendSnippet | IPlaybookActionUpdateLabel;

type IPlaybookStepBase<
  T = IPlaybookTriggerFilter | IPlaybookFilter | IPlaybookAction,
> = T & {
  children: IPlaybookNode[] | null;
};

export type IPlaybookTriggerFilterStep =
  IPlaybookStepBase<IPlaybookTriggerFilter>;
export type IPlaybookFilterStep = IPlaybookStepBase<IPlaybookFilter>;
export type IPlaybookActionStep = IPlaybookStepBase<IPlaybookAction>;

export type IPlaybookStep =
  | IPlaybookTriggerFilterStep
  | IPlaybookFilterStep
  | IPlaybookActionStep;

export type IPlaybookNode = {
  // condition: never;
  step: IPlaybookStep;
};

export interface IPlaybookV2 {
  description: string;
  first_step: IPlaybookStep;
  title: string;
  trigger: IPlaybookTrigger;
  uuid: string;
  creator_id?: number | null;
}
